import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Autocomplete,
  Slider,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import api, { ADMIN } from '../../infrastructure/config/api';

const defaultOpenForm = {
  side: 'LONG',
  notional: 20,
  leverage: 10,
  marginMode: 'CROSSED'
};

const defaultCloseNotionalForm = {
  side: 'AUTO',
  notional: 20
};

const parseMetadata = (metadata) => {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch (error) {
      return null;
    }
  }
  return metadata;
};

const AdminActionsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [symbolInput, setSymbolInput] = useState('');
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');

  const [tab, setTab] = useState(0);
  const [openForm, setOpenForm] = useState(defaultOpenForm);
  const [closeMode, setCloseMode] = useState('FULL');
  const [closePercent, setClosePercent] = useState(100);
  const [closeNotionalForm, setCloseNotionalForm] = useState(defaultCloseNotionalForm);
  const [balanceMode, setBalanceMode] = useState('V1');
  const [balancePreview, setBalancePreview] = useState(null);
  const [balanceError, setBalanceError] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);

  const [actions, setActions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSymbols, setIsLoadingSymbols] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const canSubmit = selectedUser && selectedSymbol && !isSubmitting;
  const canPreviewBalance = selectedUser && selectedSymbol && !isSubmitting && !isPreviewing;
  const canExecuteBalance = !!balancePreview && !isSubmitting;

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get(ADMIN.USERS);
      setUsers(response.data?.data || []);
      if (response.data?.data?.length && !selectedUser) {
        setSelectedUser(response.data.data[0]);
      }
    } catch (error) {
      enqueueSnackbar('Failed to load users', { variant: 'error' });
    }
  }, [enqueueSnackbar, selectedUser]);

  const fetchSymbols = useCallback(async (query) => {
    setIsLoadingSymbols(true);
    try {
      const response = await api.get(ADMIN.SYMBOLS, { params: { q: query || '' } });
      setSymbolOptions(response.data?.data || []);
    } catch (error) {
      enqueueSnackbar('Failed to load symbols', { variant: 'error' });
    } finally {
      setIsLoadingSymbols(false);
    }
  }, [enqueueSnackbar]);

  const fetchActions = useCallback(async () => {
    try {
      const params = {};
      if (selectedUser?.id) params.userId = selectedUser.id;
      if (selectedSymbol) params.symbol = selectedSymbol;
      const response = await api.get(ADMIN.ACTIONS, { params });
      setActions(response.data?.data || []);
    } catch (error) {
      enqueueSnackbar('Failed to load admin actions', { variant: 'error' });
    }
  }, [enqueueSnackbar, selectedUser, selectedSymbol]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
      fetchSymbols('');
    }
  }, [currentUser?.role, fetchUsers, fetchSymbols]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchActions();
    }
  }, [currentUser?.role, fetchActions]);

  useEffect(() => {
    setBalancePreview(null);
    setBalanceError('');
  }, [selectedUser, selectedSymbol, balanceMode]);

  const handleSymbolInput = (_event, value) => {
    setSymbolInput(value);
    if (value && value.length >= 2) {
      fetchSymbols(value);
    }
  };

  const openConfirm = (payload) => {
    setPendingPayload(payload);
    setConfirmOpen(true);
  };

  const submitAction = async () => {
    if (!pendingPayload) return;
    setIsSubmitting(true);
    try {
      const response = await api.post(pendingPayload.endpoint, pendingPayload.body);
      const action = response.data?.data;
      enqueueSnackbar(`Action executed (${action?.exchange_order_id || 'ok'})`, { variant: 'success' });
      fetchActions();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Action failed', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
      setConfirmOpen(false);
      setPendingPayload(null);
    }
  };

  const handleOpenMarket = () => {
    if (!canSubmit) return;
    const payload = {
      endpoint: ADMIN.OPEN_MARKET,
      body: {
        userId: selectedUser.id,
        symbol: selectedSymbol,
        side: openForm.side,
        notionalUsdt: Number(openForm.notional),
        leverage: Number(openForm.leverage),
        marginMode: openForm.marginMode,
        confirmAction: 'OPEN_MARKET',
        confirmSymbol: selectedSymbol,
        clientOrderId: uuidv4()
      }
    };
    openConfirm(payload);
  };

  const handleCloseMarket = () => {
    if (!canSubmit) return;
    const payload = {
      endpoint: ADMIN.CLOSE_MARKET,
      body: {
        userId: selectedUser.id,
        symbol: selectedSymbol,
        closeMode,
        percent: closeMode === 'PERCENT' ? closePercent : 100,
        confirmAction: 'CLOSE_MARKET',
        confirmSymbol: selectedSymbol,
        clientOrderId: uuidv4()
      }
    };
    openConfirm(payload);
  };

  const handleCloseMarketByNotional = () => {
    if (!canSubmit) return;
    const notionalUsdt = Number(closeNotionalForm.notional);
    if (!Number.isFinite(notionalUsdt) || notionalUsdt <= 0) {
      enqueueSnackbar('Notional must be greater than 0', { variant: 'warning' });
      return;
    }

    const payload = {
      endpoint: ADMIN.CLOSE_MARKET,
      body: {
        userId: selectedUser.id,
        symbol: selectedSymbol,
        closeMode: 'NOTIONAL',
        side: closeNotionalForm.side === 'AUTO' ? null : closeNotionalForm.side,
        notionalUsdt,
        confirmAction: 'CLOSE_MARKET',
        confirmSymbol: selectedSymbol,
        clientOrderId: uuidv4()
      }
    };
    openConfirm(payload);
  };

  const handleBalancePreview = async () => {
    if (!canPreviewBalance) return;
    setIsPreviewing(true);
    setBalanceError('');
    try {
      const response = await api.post(ADMIN.BALANCE_PREVIEW, {
        userId: selectedUser.id,
        symbol: selectedSymbol,
        mode: balanceMode
      });
      if (response.data?.success) {
        setBalancePreview(response.data?.data || null);
        enqueueSnackbar('Preview loaded', { variant: 'success' });
      } else {
        setBalancePreview(null);
        setBalanceError(response.data?.error || 'Conditions not met');
      }
    } catch (error) {
      setBalancePreview(null);
      setBalanceError(error.response?.data?.error || 'Preview failed');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleBalanceExecute = () => {
    if (!canExecuteBalance) return;
    const payload = {
      endpoint: ADMIN.BALANCE_EXECUTE,
      body: {
        userId: selectedUser.id,
        symbol: selectedSymbol,
        mode: balanceMode,
        clientActionId: uuidv4()
      },
      meta: {
        type: 'BALANCE',
        mode: balanceMode,
        steps: balancePreview?.steps || []
      }
    };
    openConfirm(payload);
  };

  if (!currentUser || currentUser.role !== 'admin' || currentUser.username !== 'devk') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Admin access required</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2}>
        <Typography variant="h5">Admin Actions</Typography>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Autocomplete
                  fullWidth
                  options={users}
                  getOptionLabel={(option) => `${option.username} (${option.role})`}
                  value={selectedUser}
                  onChange={(_event, value) => setSelectedUser(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select User" />
                  )}
                />
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={symbolOptions.map(s => s.symbol)}
                  inputValue={symbolInput}
                  onInputChange={handleSymbolInput}
                  value={selectedSymbol}
                  onChange={(_event, value) => {
                    const next = value ? String(value).toUpperCase() : '';
                    setSelectedSymbol(next);
                    setSymbolInput(next);
                  }}
                  loading={isLoadingSymbols}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Symbol"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingSymbols ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Stack>

              <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Open Market" />
                <Tab label="Close Market" />
                <Tab label="Close By Notional" />
                <Tab label="Balance / Hedge" />
              </Tabs>

              {tab === 0 && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Side</InputLabel>
                      <Select
                        value={openForm.side}
                        label="Side"
                        onChange={(event) => setOpenForm((prev) => ({ ...prev, side: event.target.value }))}
                      >
                        <MenuItem value="LONG">LONG</MenuItem>
                        <MenuItem value="SHORT">SHORT</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Notional (USDT)"
                      type="number"
                      value={openForm.notional}
                      onChange={(event) => setOpenForm((prev) => ({ ...prev, notional: event.target.value }))}
                    />
                    <TextField
                      fullWidth
                      label="Leverage"
                      type="number"
                      value={openForm.leverage}
                      onChange={(event) => setOpenForm((prev) => ({ ...prev, leverage: event.target.value }))}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Margin Mode</InputLabel>
                      <Select
                        value={openForm.marginMode}
                        label="Margin Mode"
                        onChange={(event) => setOpenForm((prev) => ({ ...prev, marginMode: event.target.value }))}
                      >
                        <MenuItem value="ISOLATED">ISOLATED</MenuItem>
                        <MenuItem value="CROSSED">CROSSED</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Button
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={handleOpenMarket}
                  >
                    Open Market
                  </Button>
                </Stack>
              )}

              {tab === 1 && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <FormControl fullWidth>
                      <InputLabel>Close Mode</InputLabel>
                      <Select
                        value={closeMode}
                        label="Close Mode"
                        onChange={(event) => setCloseMode(event.target.value)}
                      >
                        <MenuItem value="FULL">FULL</MenuItem>
                        <MenuItem value="PERCENT">PERCENT</MenuItem>
                      </Select>
                    </FormControl>
                    {closeMode === 'PERCENT' && (
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption">Percent: {closePercent}%</Typography>
                        <Slider
                          value={closePercent}
                          onChange={(_event, value) => setClosePercent(value)}
                          min={1}
                          max={100}
                        />
                      </Box>
                    )}
                  </Stack>
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={!canSubmit}
                    onClick={handleCloseMarket}
                  >
                    Close Market
                  </Button>
                </Stack>
              )}

              {tab === 2 && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Side</InputLabel>
                      <Select
                        value={closeNotionalForm.side}
                        label="Side"
                        onChange={(event) => setCloseNotionalForm((prev) => ({ ...prev, side: event.target.value }))}
                      >
                        <MenuItem value="AUTO">AUTO</MenuItem>
                        <MenuItem value="LONG">LONG</MenuItem>
                        <MenuItem value="SHORT">SHORT</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Close Notional (USDT)"
                      type="number"
                      value={closeNotionalForm.notional}
                      onChange={(event) => setCloseNotionalForm((prev) => ({ ...prev, notional: event.target.value }))}
                      helperText="Dong bot vi the theo notional. Hedge 2 dau nen chon LONG/SHORT."
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={!canSubmit}
                    onClick={handleCloseMarketByNotional}
                  >
                    Close Market By Notional
                  </Button>
                </Stack>
              )}

              {tab === 3 && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Balance Mode</InputLabel>
                      <Select
                        value={balanceMode}
                        label="Balance Mode"
                        onChange={(event) => setBalanceMode(event.target.value)}
                      >
                        <MenuItem value="V1">Reduce Loss & Rebalance (V1)</MenuItem>
                        <MenuItem value="V2">Lock Small Profit (V2)</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      disabled={!canPreviewBalance}
                      onClick={handleBalancePreview}
                    >
                      {isPreviewing ? 'Loading...' : 'Preview'}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={!canExecuteBalance}
                      onClick={handleBalanceExecute}
                    >
                      Execute Balance
                    </Button>
                  </Stack>

                  {balanceError && (
                    <Card sx={{ borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="warning.main">Warning</Typography>
                        <Typography variant="body2">{balanceError}</Typography>
                      </CardContent>
                    </Card>
                  )}

                  {balancePreview && (
                    <Stack spacing={2}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1">Snapshot</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">LONG</Typography>
                              <Typography variant="body2">
                                {balancePreview.before?.longQty?.toFixed?.(4) || balancePreview.before?.longQty} · PnL {balancePreview.before?.longPnl?.toFixed?.(2) || balancePreview.before?.longPnl} USDT
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">SHORT</Typography>
                              <Typography variant="body2">
                                {balancePreview.before?.shortQty?.toFixed?.(4) || balancePreview.before?.shortQty} · PnL {balancePreview.before?.shortPnl?.toFixed?.(2) || balancePreview.before?.shortPnl} USDT
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Total PnL</Typography>
                              <Typography variant="body2">
                                {balancePreview.before?.totalPnl?.toFixed?.(2) || balancePreview.before?.totalPnl} USDT
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1">Steps</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={1}>
                            {balancePreview.steps?.map((step, idx) => (
                              <Box key={`${step.type}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">
                                  {idx + 1}. {step.type} {step.side}
                                </Typography>
                                <Typography variant="body2">
                                  Qty {step.quantity}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Keep Ratio: {balancePreview.keepRatio?.toFixed?.(4) || balancePreview.keepRatio}
                          </Typography>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1">Expected After</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">LONG</Typography>
                              <Typography variant="body2">
                                {balancePreview.after?.longQty?.toFixed?.(4) || balancePreview.after?.longQty}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">SHORT</Typography>
                              <Typography variant="body2">
                                {balancePreview.after?.shortQty?.toFixed?.(4) || balancePreview.after?.shortQty}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">History</Typography>
              <Divider />
              <Stack spacing={1}>
                {actions.map((action) => {
                  const metadata = parseMetadata(action.metadata);
                  const isBalance = action.action_type?.includes('BALANCE_HEDGE');
                  return (
                    <Box
                      key={action.id}
                      sx={{
                        border: '1px solid #1e2233',
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        justifyContent: 'space-between'
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="subtitle2">
                          {action.symbol} · {action.action_type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(action.created_at).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Admin: {action.admin_username || action.admin_id} → User: {action.user_username || action.user_id}
                        </Typography>
                        {isBalance && metadata && (
                          <Typography variant="caption" color="text.secondary">
                            Before L {metadata.before?.longQty ?? '-'} / S {metadata.before?.shortQty ?? '-'} → After L {metadata.after?.longQty ?? '-'} / S {metadata.after?.shortQty ?? '-'}
                          </Typography>
                        )}
                      </Stack>
                      <Stack spacing={0.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                        <Chip
                          label={action.status}
                          color={action.status === 'EXECUTED' ? 'success' : action.status === 'FAILED' ? 'error' : 'warning'}
                          size="small"
                        />
                        <Typography variant="caption">
                          {action.side || '-'} · {action.notional_usdt || '-'} USDT · Lev {action.leverage || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Order: {action.exchange_order_id || '-'}
                        </Typography>
                        {action.error_message && (
                          <Typography variant="caption" color="error">
                            {action.error_message}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {pendingPayload && (
              <>
                <Typography variant="body2">
                  User: {selectedUser?.username} · Symbol: {selectedSymbol}
                </Typography>
                <Typography variant="body2">
                  {pendingPayload.meta?.type === 'BALANCE'
                    ? `Balance ${pendingPayload.meta.mode} · Steps: ${pendingPayload.meta.steps?.length || 0}`
                    : pendingPayload.endpoint.includes('open')
                      ? `Open ${openForm.side} ${openForm.notional} USDT, Lev ${openForm.leverage}, ${openForm.marginMode}`
                      : `Close ${closeMode === 'PERCENT' ? `${closePercent}%` : 'FULL'} position`}
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={submitAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminActionsPage;
